import React, { useCallback } from 'react';
import domtoimage from 'dom-to-image';
// Polls for the element instead of checking once, in case the tree hasn't mounted yet.
const waitForElement = (selectors, { timeoutMs = 2000, intervalMs = 150 } = {}) => {
  return new Promise((resolve) => {
    const start = Date.now();
    const check = () => {
      let el = null;
      for (const selector of selectors) {
        el = document.querySelector(selector);
        if (el) break;
      }
      if (el) {
        resolve(el);
        return;
      }
      if (Date.now() - start >= timeoutMs) {
        resolve(null);
        return;
      }
      setTimeout(check, intervalMs);
    };
    check();
  });
};

export const useTreeToSvgConverter = (options = {}) => {
  const [svgData, setSvgData] = React.useState(new Map());
  const [isConverting, setIsConverting] = React.useState(false);
  const [errors, setErrors] = React.useState([]);

  const defaultOptions = {
    quality: 1,
    bgcolor: 'transparent',
    cacheBust: true,
    ...options.options
  };

  const convertTreeToSvg = useCallback(async (sessionId, retryCount = 0) => {
    const maxRetries = 2;
    const retryDelay = 500;
    const BUFFER_PADDING = 20;

    try {
      // Find the tree element - try multiple selectors
      const selectors = [
        `.tree-display[data-session-id="${sessionId}"]`,
        `[data-session-id="${sessionId}"]`,
        `#tree-${sessionId}`
      ];

      const treeElement = await waitForElement(selectors, { timeoutMs: 4000, intervalMs: 150 });

      if (!treeElement) {
        const err = new Error(`No tree available to capture for session ${sessionId}`);
        err.terminal = true;
        err.noTreeAvailable = true;
        throw err;
      }

      const backgroundElement = treeElement.querySelector('.background');
      const actualWidth = backgroundElement ? backgroundElement.scrollWidth : treeElement.scrollWidth;
      const actualHeight = backgroundElement ? backgroundElement.scrollHeight : treeElement.scrollHeight;

      // Validate element has dimensions
      if (actualWidth === 0 || actualHeight === 0) {
        throw new Error(`Tree element has no dimensions (${actualWidth}x${actualHeight})`);
      }

      // Wait for rendering to complete
      await new Promise(resolve => setTimeout(resolve, 100));
      const CAPTURE_WIDTH = actualWidth + BUFFER_PADDING * 2;
      const CAPTURE_HEIGHT = actualHeight + BUFFER_PADDING * 2;

      const svgDataUrl = await domtoimage.toSvg(treeElement, {
        ...defaultOptions,
        width: CAPTURE_WIDTH,
        height: CAPTURE_HEIGHT,
        style: {
          padding: `${BUFFER_PADDING}px`,
          boxSizing: 'content-box',
          transform: 'none',
          width: `${CAPTURE_WIDTH}px`,
          height: `${CAPTURE_HEIGHT}px`,
        },
      });

      // Validate SVG
      if (!svgDataUrl || !svgDataUrl.startsWith('data:image/svg+xml')) {
        throw new Error('Invalid SVG data generated');
      }

      // Store in state
      setSvgData(prev => new Map(prev.set(sessionId, svgDataUrl)));

      return svgDataUrl;

    } catch (error) {
      if (error.noTreeAvailable) {
        throw error;
      }

      console.error(`Error converting tree ${sessionId} (attempt ${retryCount + 1}):`, error);

      if (error.terminal) {
        setErrors(prev => [...prev, { sessionId, error, timestamp: Date.now() }]);
        throw error;
      }

      if (retryCount < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        return convertTreeToSvg(sessionId, retryCount + 1);
      }

      // Store error
      setErrors(prev => [...prev, { sessionId, error, timestamp: Date.now() }]);
      throw error;
    }
  }, [defaultOptions]);

  const convertMultipleTrees = useCallback(async (sessionIds) => {
    setIsConverting(true);
    const results = [];

    // Process sequentially to avoid overwhelming browser
    for (const sessionId of sessionIds) {
      try {
        const svgDataUrl = await convertTreeToSvg(sessionId);
        results.push({ sessionId, svgDataUrl, success: true });
      } catch (error) {
        results.push({ sessionId, svgDataUrl: null, success: false, error });
      }
    }

    setIsConverting(false);
    return { results, errors: results.filter(r => !r.success) };
  }, [convertTreeToSvg]);

  const clearSvgData = useCallback((sessionId) => {
    if (sessionId) {
      setSvgData(prev => {
        const newMap = new Map(prev);
        newMap.delete(sessionId);
        return newMap;
      });
    } else {
      setSvgData(new Map());
    }
  }, []);

  return {
    svgData,
    isConverting,
    errors,
    convertTreeToSvg,
    convertMultipleTrees,
    clearSvgData
  };
};

export default useTreeToSvgConverter;